import { apiClient } from "./authService";

/**
 * Servicio para gestión de grupos
 * Todas las peticiones incluyen automáticamente token JWT via interceptores
 *
 * SEGUIR REGLAS:
 * - Usar apiClient con autenticación automática
 * - Estructura CRUD estándar según PLANIFICACION-SISTEMA.md
 * - TypeScript estricto
 * - Manejo de errores centralizado
 */

export interface Group {
  id: string;
  nombre: string;
  descripcion: string;
  activo: boolean;
  es_sistema: boolean;
  fecha_creacion: string;
  fecha_actualizacion: string;
}

export interface CreateGroupDto {
  nombre: string;
  descripcion: string;
  activo?: boolean;
}

export interface UpdateGroupDto {
  nombre?: string;
  descripcion?: string;
  activo?: boolean;
}

/**
 * Obtener todos los grupos con paginación
 * Token JWT automático via interceptores
 */
export async function getGroups(
  page: number = 1,
  limit: number = 100,
): Promise<Group[]> {
  try {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());

    const response = await apiClient.get<{
      groups: Group[];
      total: number;
      page: number;
      limit: number;
    }>(`/groups?${params.toString()}`);
    return response.data.groups;
  } catch (error) {
    console.error("Error obteniendo grupos:", error);
    throw new Error("No se pudieron cargar los grupos");
  }
}

/**
 * Obtener grupo por ID
 */
export async function getGroupById(id: string): Promise<Group> {
  try {
    const response = await apiClient.get<Group>(`/groups/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error obteniendo grupo ${id}:`, error);
    throw new Error("No se pudo cargar el grupo");
  }
}

/**
 * Crear nuevo grupo
 */
export async function createGroup(data: CreateGroupDto): Promise<Group> {
  try {
    const response = await apiClient.post<Group>("/groups", data);
    return response.data;
  } catch (error) {
    console.error("Error creando grupo:", error);
    throw new Error("No se pudo crear el grupo");
  }
}

/**
 * Actualizar grupo existente
 */
export async function updateGroup(
  id: string,
  data: UpdateGroupDto,
): Promise<Group> {
  try {
    const response = await apiClient.patch<Group>(`/groups/${id}`, data);
    return response.data;
  } catch (error) {
    console.error(`Error actualizando grupo ${id}:`, error);
    throw new Error("No se pudo actualizar el grupo");
  }
}

/**
 * Eliminar grupo
 */
export async function deleteGroup(id: string): Promise<void> {
  try {
    await apiClient.delete(`/groups/${id}`);
  } catch (error) {
    console.error(`Error eliminando grupo ${id}:`, error);
    throw new Error("No se pudo eliminar el grupo");
  }
}

/**
 * Cambiar estado activo/inactivo del grupo
 */
export async function toggleGroupStatus(
  id: string,
  activo: boolean,
): Promise<Group> {
  try {
    const response = await apiClient.patch<Group>(`/groups/${id}`, { activo });
    return response.data;
  } catch (error) {
    console.error(`Error cambiando estado del grupo ${id}:`, error);
    throw new Error("No se pudo cambiar el estado del grupo");
  }
}

/**
 * Servicio exportado con todas las funciones
 */
export const groupsService = {
  getGroups,
  getGroupById,
  createGroup,
  updateGroup,
  deleteGroup,
  toggleGroupStatus,
};
